<template>
  <div class="page-container billing-transfer-page">
    <portal to="page-name">Repasses</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <div class="tr-header-actions">
          <span class="tooltip tooltip-left" :data-tooltip="reportTooltip">
            <button class="btn btn-secondary btn-icon btn-icon-left"
                    :class="{loading: reporting === 'pdf'}" :disabled="reportDisabled"
                    @click="report('pdf')">
              <fa-icon :icon="['fal', 'print']"/>Relatório
            </button>
          </span>
          <span class="tooltip tooltip-left" :data-tooltip="spreadsheetTooltip">
            <button class="btn btn-gray btn-icon btn-icon-left"
                    :class="{loading: reporting === 'xlsx'}" :disabled="reportDisabled"
                    @click="report('xlsx')">
              <fa-icon :icon="['fal', 'file-excel']"/>Excel
            </button>
          </span>
        </div>
        <h1 class="card-title">Repasse dos profissionais</h1>
      </div>

      <div class="card-body">
        <transfer-filters v-model="filter" :validator="$v.filter"
                          :professionals="professionals" :insurances="insurances"
                          :loading="loading" @search="search"/>

        <div class="loading loading-lg mt-2" v-if="loading && !loaded"></div>
        <template v-else>
          <transfer-summary :totals="data.totals" :counts="counts" :period="data.period"
                            :mode="appliedFilter.mode" :statuses="appliedFilter.statuses"
                            :origin-filter="appliedFilter.origin" :loading="loading"/>

          <div class="loading loading-lg tr-list-loading" v-if="loading"></div>
          <template v-else>
            <transfer-empty v-if="data.professionals.length === 0"
                            :filter="appliedFilter" :scope-label="scopeLabel"
                            :professional-name="appliedProfessionalName"
                            :insurance-name="appliedInsuranceName"
                            @all-statuses="includeAllStatuses"
                            @clear-filters="clearNarrowFilters"/>
            <template v-else>
              <professional-group v-for="group in data.professionals"
                                  :key="String(group.professional.id)"
                                  :group="group" :selected-keys="selectedKeys"
                                  :origin-filter="appliedFilter.origin"
                                  :can-pay="canPay" :can-print="canPrint" :can-consolidate="canPay"
                                  :default-open="data.professionals.length <= 3"
                                  :printing-entry-id="printingEntryId"
                                  @select="select"
                                  @consolidate="consolidateRows"
                                  @receipt="openReceipt"
                                  @print="openRowReceipt"
                                  @pay="pay"
                                  @open-batch="openBatch"
                                  @open-entry="printEntry"
                                  @open-report="openReport"/>
            </template>
          </template>

          <selection-bar v-if="selected.length > 0"
                         :count="selected.length" :professionals="selectedProfessionals"
                         :total="selectedTotal" :can-consolidate="canPay"
                         @clear="clearSelection" @consolidate="openConsolidate"/>
        </template>
      </div>

      <div class="card-footer" v-if="hasData">
        <span class="tr-footer-item">
          Profissionais: <strong class="text-primary">{{ data.totals.professionals }}</strong>
        </span>
        <span class="tr-footer-item" v-if="appliedFilter.statuses.includes('pending')">
          Pendente de consolidar:
          <strong class="text-primary">{{ data.totals.pending | currency }}</strong>
        </span>
        <span class="tr-footer-item" v-if="appliedFilter.statuses.includes('consolidated')">
          Consolidado a pagar:
          <strong class="text-primary">{{ data.totals.consolidated | currency }}</strong>
        </span>
        <span class="tr-footer-item" v-if="appliedFilter.statuses.includes('paid')">
          Pago no período: <strong class="text-primary">{{ data.totals.paid | currency }}</strong>
        </span>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <consolidate-modal v-if="consolidate.show" :show="consolidate.show"
                       :groups="consolidate.groups" :defaults="consolidateDefaults"
                       @close="closeConsolidate" @saved="consolidated"/>
    <receipt-modal v-if="receipt.show" :show="receipt.show"
                   :professional="receipt.professional" :financials="receipt.financials"
                   :pending="receipt.pending" :single="receipt.single"
                   @close="receipt.show = false"/>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
  date, minDate, maxDate, competenceDate,
} from '@/data/validators';
import {
  LIST_BILLING_TRANSFERS,
  PAY_BILLING_TRANSFER,
  PRINT_BILLING_TRANSFER,
} from '@/data/actions/modules/financial';
import { statuses } from '@/data/billing-transfer-statuses';
import * as professionalApi from '@/app/professional/api';
import Forbidden from '@/components/auth/Forbidden.vue';
import TransferFilters from './TransferFilters.vue';
import TransferSummary from './TransferSummary.vue';
import TransferEmpty from './TransferEmpty.vue';
import ProfessionalGroup from './ProfessionalGroup.vue';
import SelectionBar from './SelectionBar.vue';
import ConsolidateModal from '../modals/Consolidate.vue';
import ReceiptModal from '../modals/Receipt.vue';
import {
  competenceLabel, groupSelection, isSelectable, selectionKey,
} from '../helpers';

const REPORT_FILE = 'repasses-profissionais.xlsx';

function emptyData() {
  return {
    period: null,
    professionals: [],
    totals: {
      pending: 0,
      pendingCount: 0,
      consolidated: 0,
      paid: 0,
      adjustments: 0,
      adjustmentsCount: { debit: 0, credit: 0 },
      byOrigin: { lot: 0, cashRegister: 0, medicalReport: 0 },
      professionals: 0,
    },
  };
}

function defaultFilter() {
  const previous = moment().subtract(1, 'month');
  return {
    professionalId: '',
    planId: '',
    mode: 'competence',
    competence: previous.format('MM/YYYY'),
    startDate: previous.clone().startOf('month').format('YYYY-MM-DD'),
    endDate: previous.clone().endOf('month').format('YYYY-MM-DD'),
    origin: 'all',
    statuses: ['pending', 'consolidated'],
  };
}

export default {
  components: {
    Forbidden,
    TransferFilters,
    TransferSummary,
    TransferEmpty,
    ProfessionalGroup,
    SelectionBar,
    ConsolidateModal,
    ReceiptModal,
  },
  data() {
    const filter = defaultFilter();
    return {
      loading: false,
      loaded: false,
      reporting: null,
      printingEntryId: null,
      professionals: [],
      insurances: [],
      filter,
      appliedFilter: { ...filter },
      data: emptyData(),
      selected: [],
      consolidate: {
        show: false,
        groups: [],
      },
      receipt: {
        show: false,
        professional: null,
        financials: [],
        pending: null,
        single: false,
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasAccess() {
      return this.$can(LIST_BILLING_TRANSFERS);
    },
    canPay() {
      return this.$can(PAY_BILLING_TRANSFER);
    },
    canPrint() {
      return this.$can(PRINT_BILLING_TRANSFER);
    },
    hasData() {
      return this.loaded && this.data.professionals.length > 0;
    },
    reportDisabled() {
      return !!this.reporting;
    },
    reportTooltip() {
      return 'Imprimir o relatório de repasses do filtro atual';
    },
    spreadsheetTooltip() {
      return 'Baixar a planilha de repasses do filtro atual';
    },
    scopeLabel() {
      if (this.appliedFilter.mode === 'period') {
        const start = moment(this.appliedFilter.startDate).format('L');
        const end = moment(this.appliedFilter.endDate).format('L');
        return `${start} a ${end}`;
      }
      return competenceLabel(this.appliedFilter.competence);
    },
    appliedProfessionalName() {
      const found = this.professionals
        .find(item => String(item.id) === String(this.appliedFilter.professionalId));
      return found ? found.name : '';
    },
    appliedInsuranceName() {
      const found = this.insurances
        .find(item => String(item.id) === String(this.appliedFilter.planId));
      return found ? found.name : '';
    },
    settings() {
      const settings = this.user && this.user.branch && this.user.branch.settings
        ? this.user.branch.settings.insuranceBilling
        : null;
      return settings || {};
    },
    consolidateDefaults() {
      return {
        paymentDays: Number(this.settings.defaultPaymentDays) || 30,
        categoryId: this.settings.transferCategoryId || null,
        costCenterId: this.settings.costCenterId || null,
      };
    },
    selectedKeys() {
      return this.selected.map(selectionKey);
    },
    selectedTotal() {
      return this.selected.reduce((sum, row) => sum + (Number(row.transfer) || 0), 0);
    },
    selectedProfessionals() {
      return new Set(this.selected.map(row => String(row.professionalId))).size;
    },
    counts() {
      const counts = {
        consolidated: 0, paid: 0, lot: 0, cashRegister: 0, medicalReport: 0,
      };
      this.data.professionals.forEach((group) => {
        counts.lot += group.origins.lot.count;
        counts.cashRegister += group.origins.cashRegister.count;
        counts.medicalReport += group.origins.medicalReport.count;
        group.origins.lot.items.forEach((row) => {
          if (row.status === 'consolidated') counts.consolidated += 1;
          if (row.status === 'paid') counts.paid += 1;
        });
        group.consolidations.forEach((row) => {
          if (row.transferStatus === 'consolidated') counts.consolidated += 1;
          if (row.transferStatus === 'paid') counts.paid += 1;
        });
      });
      return counts;
    },
  },
  validations() {
    const isPeriod = this.filter.mode === 'period';
    const { startDate } = this.filter;
    const validStart = startDate && moment(startDate, 'YYYY-MM-DD', true).isValid();
    return {
      filter: {
        statuses: { required },
        competence: {
          required: requiredIf(() => !isPeriod),
          competenceDate,
        },
        startDate: {
          required: requiredIf(() => isPeriod),
          date,
        },
        endDate: {
          required: requiredIf(() => isPeriod),
          date,
          ...(isPeriod ? {
            minDate: minDate(validStart ? startDate : null),
            maxDate: maxDate(validStart ? moment(startDate).add(6, 'months') : null),
          } : {}),
        },
      },
    };
  },
  mounted() {
    if (!this.hasAccess) return;
    this.loadProfessionals();
    this.loadInsurances();
    this.load();
  },
  methods: {
    params(filter) {
      const params = {
        origin: filter.origin,
        status: filter.statuses.join(','),
      };
      if (filter.mode === 'period') {
        params.startDate = filter.startDate;
        params.endDate = filter.endDate;
      } else {
        const competence = moment(`01/${filter.competence}`, 'DD/MM/YYYY', true);
        params.month = competence.month() + 1;
        params.year = competence.year();
      }
      if (filter.professionalId) params.professionalId = filter.professionalId;
      if (filter.planId) params.planId = filter.planId;
      return params;
    },
    search() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) return Promise.resolve();
      return this.load();
    },
    includeAllStatuses() {
      this.filter.statuses = Object.keys(statuses);
      this.search();
    },
    clearNarrowFilters() {
      this.filter.professionalId = '';
      this.filter.planId = '';
      this.filter.origin = 'all';
      this.search();
    },
    load() {
      this.loading = true;
      const applied = { ...this.filter, statuses: [...this.filter.statuses] };
      return this.$http.get('/billing-transfers', { params: this.params(applied) })
        .then(({ data }) => {
          this.appliedFilter = applied;
          this.data = { ...emptyData(), ...data };
          this.loaded = true;
          this.pruneSelection();
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadProfessionals() {
      return professionalApi.allActive()
        .then((data) => {
          this.professionals = data.items;
        })
        .catch(() => {});
    },
    loadInsurances() {
      return this.$http.get('/insurance-plans', { params: { limit: 0, active: true } })
        .then(({ data }) => {
          this.insurances = data.items.map(item => ({ id: item.plan.id, name: item.customName }));
        })
        .catch(() => {});
    },
    allRows() {
      return this.data.professionals.flatMap(group => [
        ...group.origins.cashRegister.items,
        ...group.origins.medicalReport.items,
      ]);
    },
    pruneSelection() {
      const available = new Set(this.allRows().filter(isSelectable).map(selectionKey));
      this.selected = this.selected.filter(row => available.has(selectionKey(row)));
    },
    select(rows, checked) {
      const keys = new Set(this.selectedKeys);
      const professionalOf = {};
      this.data.professionals.forEach((group) => {
        professionalOf[String(group.professional.id)] = group.professional;
      });
      if (checked) {
        const additions = rows
          .filter(isSelectable)
          .filter(row => !keys.has(selectionKey(row)))
          .map(row => ({
            ...row,
            professional: professionalOf[String(row.professionalId)] || null,
          }));
        this.selected = [...this.selected, ...additions];
      } else {
        const removals = new Set(rows.map(selectionKey));
        this.selected = this.selected.filter(row => !removals.has(selectionKey(row)));
      }
    },
    clearSelection() {
      if (this.consolidate.show || this.receipt.show) return;
      this.selected = [];
    },
    consolidateRows(rows) {
      if (!this.canPay) return;
      this.select(rows, true);
      this.openConsolidate();
    },
    openConsolidate() {
      if (!this.canPay || this.selected.length === 0) return;
      this.consolidate.groups = groupSelection(this.selected);
      this.consolidate.show = true;
    },
    closeConsolidate() {
      this.consolidate.show = false;
    },
    consolidated(result) {
      this.selected = [];
      this.load().then(() => {
        const created = result && Array.isArray(result.created) ? result.created : [];
        if (created.length === 1 && this.canPay) {
          this.offerPayment(created[0]);
        }
      });
    },
    offerPayment(created) {
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Conta a pagar criada</h5>'
          + `<div>${created.name}: ${created.document}. `
          + 'Deseja registrar o pagamento agora?</div></div>',
        buttons: [
          { label: 'Agora não', classes: '' },
          {
            label: 'Pagar agora',
            classes: 'btn-primary ml-2',
            click: (close) => {
              close();
              this.pay(created.financialId);
            },
          },
        ],
      });
    },
    pay(financialId) {
      if (!financialId || !this.canPay) return;
      this.$router.push(`/financial/transactions/${financialId}/payment`);
    },
    financialsOf(group) {
      const lots = group.origins.lot.items.map(row => ({
        id: row.financialId,
        bundleId: row.bundleId,
        label: `Lote ${row.batch && row.batch.number ? row.batch.number : ''}`.trim(),
        status: row.status,
        total: row.total,
      }));
      const consolidations = group.consolidations.map(row => ({
        id: row.id,
        bundleId: row.bundleId,
        label: row.document || 'Repasse',
        status: row.transferStatus,
        total: row.total,
      }));
      return [...lots, ...consolidations].filter(item => item.bundleId);
    },
    pendingOf(group) {
      const rows = [...group.origins.cashRegister.items, ...group.origins.medicalReport.items]
        .filter(isSelectable);
      return {
        cashRegisterItemIds: rows
          .filter(row => row.origin === 'cashRegister')
          .map(row => ({ entryId: String(row.entryId), itemId: String(row.itemId) })),
        medicalReportIds: rows
          .filter(row => row.origin === 'medicalReport')
          .map(row => String(row.reportId || row.id)),
        count: rows.length,
        total: rows.reduce((sum, row) => sum + (Number(row.transfer) || 0), 0),
      };
    },
    openReceipt(group) {
      if (!this.canPrint) return;
      this.receipt = {
        show: true,
        professional: group.professional,
        financials: this.financialsOf(group),
        pending: this.pendingOf(group),
        single: false,
      };
    },
    openRowReceipt(row) {
      if (!this.canPrint || !row.bundleId) return;
      const group = this.data.professionals.find(item => (
        String(item.professional.id) === String(row.professionalId)
      ));
      const financials = group ? this.financialsOf(group) : [];
      const financial = financials.find(item => String(item.bundleId) === String(row.bundleId))
        || {
          id: row.financialId || row.id,
          bundleId: row.bundleId,
          label: row.document || 'Repasse',
          status: row.status || row.transferStatus,
          total: row.total,
        };
      this.receipt = {
        show: true,
        professional: group ? group.professional : null,
        financials: [financial],
        pending: {
          cashRegisterItemIds: [], medicalReportIds: [], count: 0, total: 0,
        },
        single: true,
      };
    },
    openBatch(batchId) {
      if (!batchId) return;
      this.$router.push(`/financial/billing-batches/${batchId}`);
    },
    openReport(row) {
      const id = row.reportId || row.id;
      if (!id) return;
      this.$router.push(`/medical-reports/${id}`);
    },
    printEntry(row) {
      if (!row.entryId) return;
      this.printingEntryId = String(row.entryId);
      this.$file.print(`/cash-register/entries/${row.entryId}/receipt`)
        .catch(() => {})
        .finally(() => {
          this.printingEntryId = null;
        });
    },
    report(format) {
      if (!this.hasData) return;
      const params = {
        ...this.params(this.appliedFilter),
        format,
        groupBy: 'professional',
      };
      this.reporting = format;
      const request = format === 'xlsx'
        ? this.$file.download('/billing-transfers/report', params, { name: REPORT_FILE })
        : this.$file.print('/billing-transfers/report', params);
      Promise.resolve(request)
        .catch(() => {})
        .finally(() => {
          this.reporting = null;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../transfer.scss";
</style>
